import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import '../styles/globals.css';
import { useData } from '../context/DataContext';
import SubmissionsDatabase from '../components/SubmissionsDatabase';

const HomeContainer = styled.div`
  min-height: 100vh;
  background-color: var(--bg-page);
`;

const Header = styled.header`
  max-width: var(--container-width);
  margin: 0 auto;
  padding: 3rem 24px 1.5rem 24px;

  @media (max-width: 768px) {
    padding: 2rem 16px 1rem 16px;
  }
`;

const Eyebrow = styled.div`
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-accent);
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const Title = styled.h1`
  font-family: var(--font-lora);
  font-weight: 600;
  font-size: 2.25rem;
  line-height: 1.15;
  letter-spacing: -0.01em;
  margin: 0 0 0.35rem 0;
  color: var(--color-text);

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const Attribution = styled.div`
  font-size: 0.9375rem;
  color: var(--color-text-muted);
  margin: 0 0 1rem 0;

  a {
    color: var(--color-text-muted);
    text-decoration: underline;
    text-underline-offset: 2px;
    &:hover { color: var(--color-accent); }
  }
`;

const Blurb = styled.p`
  max-width: 860px;
  font-size: 1.0625rem;
  line-height: 1.6;
  color: var(--color-text-muted);
  margin: 0;

  a {
    color: var(--color-accent);
    text-decoration: underline;
    text-underline-offset: 2px;
    &:hover { color: var(--color-accent-hover); }
  }
`;

const LoadingState = styled.div`
  max-width: var(--container-width);
  margin: 0 auto;
  padding: 6rem 24px;
  text-align: center;
  color: var(--color-text-muted);
  font-family: var(--font-lora);
  font-style: italic;
  font-size: 1.0625rem;
`;

const ErrorState = styled.div`
  max-width: var(--container-width);
  margin: 0 auto;
  padding: 4rem 24px;
  text-align: center;
  color: var(--color-text);

  pre {
    text-align: left;
    background: var(--bg-card);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    padding: 1rem;
    margin: 1rem 0;
    overflow-x: auto;
    font-size: 0.875rem;
  }
`;

function Home() {
  const { loading, error } = useData();

  if (error) {
    return (
      <HomeContainer>
        <ErrorState>
          <h2>Failed to load submission data</h2>
          <pre>{String(error.message || error)}</pre>
          <p>
            Run <code>python3 build_site_data.py</code> from the repo root to
            generate the static data assets.
          </p>
        </ErrorState>
      </HomeContainer>
    );
  }

  return (
    <HomeContainer>
      <Header>
        <Eyebrow>DOL Prevailing Wage NPRM · Docket ETA-2026-0001</Eyebrow>
        <Title>Every public comment on the prevailing wage rule</Title>
        <Attribution>
          A project by the{' '}
          <a href="https://www.ifp.org/" target="_blank" rel="noopener noreferrer">
            Institute for Progress
          </a>
        </Attribution>
        <Blurb>
          On March 27th, 2026 the Department of Labor published a proposed rule on
          Improving Wage Protections for the Temporary and Permanent Employment of
          Certain Foreign Nationals in the United States (
          <a href="https://www.regulations.gov/docket/ETA-2026-0001" target="_blank" rel="noopener noreferrer">
            RIN 1205-AC30
          </a>
          ), proposing changes to prevailing wages and affecting eligibility for
          visa programs including EB-2 and EB-3 employment-based immigrant visas
          subject to PERM and H-1B, H-1B1, or E-3 nonimmigrant visas. Over 1,000
          stakeholders replied to the agency in public comments, with over 100
          stakeholders providing attachments with specific, formal comments. The
          database below summarizes the concerns and recommendations shared with
          the department. We <Link to="/methodology">used AI</Link> to extract
          recommendations and topics discussed from each submission, and to tag
          them with relevant information. Search the database and click on a
          comment to learn more about it.
        </Blurb>
      </Header>
      {loading ? (
        <LoadingState>Loading submissions…</LoadingState>
      ) : (
        <SubmissionsDatabase />
      )}
    </HomeContainer>
  );
}

export default Home;
